<template>
  <v-navigation-drawer
    :model-value="drawer"
    color="sidebar"
    width="264"
    mobile-breakpoint="md"
    class="int-sidebar"
    @update:model-value="$emit('update:drawer', $event)"
  >
    <!-- Brand plate -->
    <div class="int-brand-plate">
      <div class="int-logo-plate">
        <v-img
          :src="require('../assets/int_logo.png')"
          height="40"
          contain
        ></v-img>
      </div>
    </div>

    <v-list nav class="px-2 py-2">
      <template v-for="group in filteredGroups" :key="group.label || group.items[0][1]">
        <v-list-subheader v-if="group.label" class="int-subheader">{{ group.label }}</v-list-subheader>
        <v-list-item
          v-for="[icon, text] in group.items"
          :key="text"
          :prepend-icon="icon"
          :title="text"
          :active="selected === text"
          class="int-nav-item mb-1"
          rounded="lg"
          @click="SelectedNav(text)"
        ></v-list-item>
      </template>
    </v-list>

    <template v-slot:append>
      <div class="pa-4">
        <div class="int-tip text-caption">
          <v-icon size="16" color="primary" class="mr-1">mdi-lightbulb-on-outline</v-icon>
          INT Ticketing
          <div class="int-tip-sub">Logistics &amp; Manpower</div>
        </div>
      </div>
    </template>
  </v-navigation-drawer>
</template>

<script>

export default {
  name: 'SidebarPage',
  props: {
    selected: { type: String, default: 'Home' },
    drawer: { type: Boolean, default: true },
  },
  emits: ['toggle-sidebar', 'update:drawer'],
  data: () => ({
    user_data: { email: '' },
    navGroups: [
      {
        label: '',
        items: [
          ['mdi-view-dashboard-outline', 'Home', 'dashboard'],
          ['mdi-account-voice', 'Daily Check-In', 'checkin'],
          ['mdi-folder-multiple-outline', 'Projects', 'projects'],
          ['mdi-chart-timeline', 'Project Timeline', 'timeline'],
          ['mdi-run-fast', 'Sprints', 'sprints'],
        ],
      },
      {
        label: 'SUPPORT',
        items: [
          ['mdi-view-column-outline', 'Board', 'board'],
          ['mdi-ticket-outline', 'All Project Tickets', 'tickets'],
          ['mdi-lifebuoy', 'IT Support', 'it'],
          ['mdi-test-tube', 'QA / Test Cases', 'qa'],
        ],
      },
      {
        label: 'CONFIGURATIONS',
        items: [
          ['mdi-account-group-outline', 'Teams', 'teams'],
          ['mdi-account-cog-outline', 'User Management', 'users'],
        ],
      },
    ],
  }),
  computed: {
    // Allowed module keys from the signed-in account (null/empty = no
    // restriction, so existing accounts see everything).
    allowedModules() {
      const m = this.user_data && this.user_data.modules;
      return Array.isArray(m) && m.length ? m : null;
    },
    // Nav groups filtered to the user's allowed modules; empty groups drop out.
    filteredGroups() {
      const allowed = this.allowedModules;
      if (!allowed) return this.navGroups;
      return this.navGroups
        .map((g) => ({ label: g.label, items: g.items.filter((it) => !it[2] || allowed.includes(it[2])) }))
        .filter((g) => g.items.length);
    },
    displayName() {
      const u = this.user_data || {};
      if (u.employee_information && u.employee_information.fullname) {
        return u.employee_information.fullname;
      }
      return u.email ? u.email.split('@')[0] : 'User';
    },
    initials() {
      const name = this.displayName || 'U';
      const parts = name.replace(/[^a-zA-Z ]/g, '').trim().split(/\s+/);
      return ((parts[0]?.[0] || '') + (parts[1]?.[0] || '')).toUpperCase() || 'U';
    },
  },
  methods: {
    SelectedNav(selected) {
      this.$emit('toggle-sidebar', selected);
      // On mobile the drawer overlays content — close it after navigating.
      if (this.$vuetify.display.mdAndDown) {
        this.$emit('update:drawer', false);
      }
    },
  },
  created() {
    const userData = localStorage.getItem('user_data');
    if (userData) {
      try { this.user_data = JSON.parse(userData); } catch (e) { this.user_data = { email: '' }; }
    } else {
      console.error('User data not found in local storage');
    }
  },
}
</script>

<style scoped>
/* Cream sidebar mirrored from the Claims CMS (INT Portal look):
   light background, white left-rounded "pill" active items with blue text. */
.int-brand-plate {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 16px 16px 12px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}
.int-logo-plate {
  background: #ffffff;
  border-radius: 12px;
  padding: 8px 14px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.12);
  width: 100%;
}

.int-user-name {
  color: #2b2b2b;
}
.int-user-mail {
  color: rgba(0, 0, 0, 0.5);
  font-size: 0.72rem;
}

.int-divider {
  border-color: rgba(0, 0, 0, 0.08) !important;
}

.int-subheader {
  color: rgba(0, 0, 0, 0.45) !important;
  font-size: 0.68rem !important;
  font-weight: 700;
  letter-spacing: 0.12em;
  min-height: 28px !important;
}

/* Nav rail: full-width rows so the active pill can sit flush to the right edge. */
.int-sidebar :deep(.v-list) {
  padding-right: 0 !important;
}
.int-sidebar :deep(.int-nav-item) {
  color: #2b2b2b;
  border-radius: 22px 0 0 22px !important;
  margin-right: 0 !important;
  margin-left: 0 !important;
}
.int-sidebar :deep(.int-nav-item .v-list-item-title) {
  font-size: 0.9rem;
  font-weight: 500;
}
.int-sidebar :deep(.int-nav-item .v-icon) {
  color: #6b6b6b;
}
.int-sidebar :deep(.int-nav-item:hover:not(.v-list-item--active)) {
  background: rgba(255, 255, 255, 0.55);
}
/* Active = white pill, blue text + icon, subtle lift. */
.int-sidebar :deep(.int-nav-item.v-list-item--active) {
  background: #ffffff;
  color: var(--int-sidebar-active);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.10);
}
.int-sidebar :deep(.int-nav-item.v-list-item--active .v-list-item-title) {
  font-weight: 700;
}
.int-sidebar :deep(.int-nav-item.v-list-item--active .v-icon) {
  color: var(--int-sidebar-active);
}
/* Vuetify draws a tinted overlay on active items — suppress it so the pill
   reads as clean white rather than yellow-tinted. */
.int-sidebar :deep(.int-nav-item.v-list-item--active .v-list-item__overlay) {
  opacity: 0 !important;
}

.int-tip {
  background: rgba(255, 255, 255, 0.6);
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: 10px;
  padding: 10px 12px;
  color: #4a4a4a;
  font-weight: 600;
}
.int-tip-sub {
  color: rgba(0, 0, 0, 0.45);
  font-weight: 400;
  font-size: 0.7rem;
  margin-top: 2px;
}
</style>
